import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useMemo, useState } from "react";
import { searchByProp } from "utils/search";
import { FETCH_COMPLIANCE_CERTIFICATES_FOR_SELECTOR } from "pages/manage/products/All/graphql";

const ComplianceCertificatesSelector = ({
  onDone,
  defaultSelectedCertificates = [],
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedCertificates, setSelectedCertificates] = useState(
    defaultSelectedCertificates,
  );
  const selectedIdSet = useMemo(
    () => new Set(selectedCertificates.map((certificate) => certificate.id)),
    [selectedCertificates],
  );

  const { loading, error, data } = useQuery(
    FETCH_COMPLIANCE_CERTIFICATES_FOR_SELECTOR,
    {
      variables: { q: searchText || undefined },
      fetchPolicy: "network-only",
    },
  );

  function toggleCertificate(certificate) {
    if (selectedIdSet.has(certificate.id)) {
      setSelectedCertificates((prev) =>
        prev.filter((item) => item.id !== certificate.id),
      );
      return;
    }
    setSelectedCertificates((prev) => [...prev, certificate]);
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const certificates = (data?.certificates?.results || []).filter(
    (certificate) =>
      searchByProp(certificate, ["title", "certificateNumber"], searchText),
  );

  return (
    <div className="flex flex-col flex-1 max-h-[60vh]">
      <SearchBar
        bgColor="dark:bg-gray-900"
        autoFocus
        type="text"
        placeholder="Search by title or certificate number"
        value={searchText}
        onChange={setSearchText}
      />
      <div className="flex-1 mt-4 overflow-auto">
        {certificates.map((certificate) => (
          <div
            key={certificate.id}
            className="flex items-center gap-3 border-b dark:border-gray-700 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer py-2"
            onClick={() => toggleCertificate(certificate)}
          >
            <input
              type="checkbox"
              checked={selectedIdSet.has(certificate.id)}
              onChange={() => toggleCertificate(certificate)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex-1">
              {certificate.certificateNumber ? (
                <div className="text-xs opacity-70">
                  #{certificate.certificateNumber}
                </div>
              ) : null}
              <div>
                {certificate.title || "-"}{" "}
                {certificate.certificateType
                  ? `(${certificate.certificateType})`
                  : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="pt-4 mt-2 border-t dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-xs opacity-70">
            {selectedCertificates.length} certificate
            {selectedCertificates.length === 1 ? "" : "s"} selected
          </div>
          {selectedCertificates.length > 0 ? (
            <Button
              danger
              className="text-xs"
              onClick={() => setSelectedCertificates([])}
            >
              Clear Selection
            </Button>
          ) : null}
        </div>
        <Button primary onClick={() => onDone(selectedCertificates)}>
          Done
        </Button>
      </div>
    </div>
  );
};

export default ComplianceCertificatesSelector;
